import React, { Dispatch, ReactElement, createContext, useContext, useEffect, useReducer } from 'react';
import { DataStore, StoreActions, DataStoreReducer } from '../reducers';
import { JiraProject } from '../common/Types';
import { JIRA_EPIC_KEY, NO_EPIC_KEY } from '../common';
import Strings from '../common/Strings';
import { initialGenerationFormState } from '../reducers/GenerationFormStore/GenerationFormStore.types';
import { IssueTypeDetector, DEFAULT_ISSUE_TYPE_AVAILABILITY } from '../services/IssueTypeService';
import { AppStoreActionTypes } from '../reducers/AppStore/AppStore.types';

const initialStoreValue: DataStore = {
  appState: {
    isLoading: false,
    loadingMessage: Strings.GENERATE_LOADER_MESSAGE,
    isHomePage: true,
    recentProject: null,
    issueTypeAvailability: DEFAULT_ISSUE_TYPE_AVAILABILITY,
    isPublishSuccess: false,
    isPublishError: false,
    publishErrorMessage: ''
  },
  userStoryState: {
    epics: {},
    jiraEpics: {},
    stories: {},
    epicToStoryMap: {
      [NO_EPIC_KEY]: [],
      [JIRA_EPIC_KEY]: []
    },
    selectedEpic: null,
    selectedStories: [],
    selectedEpics: [],
    nextEpicIncrementKey: 1,
    nextStoryIncrementKey: 1,
    notes: [],
    currentProject: {} as JiraProject,
    selectedStoryIdToEdit: '',
    selectedEpicIdToEdit: '',
    sectionCreationOrder: []
  },
  generationFormState: initialGenerationFormState
};

const DataStoreContext = createContext<{
  state: DataStore;
  dispatch: Dispatch<StoreActions>;
}>({
  state: initialStoreValue,
  dispatch: () => null
});

type DataStoreProviderProps = {
  children: ReactElement;
  enableIssueTypeDetection?: boolean;
};

export const DataStoreProvider: React.FC<DataStoreProviderProps> = ({
  children,
  enableIssueTypeDetection = true
}) => {
  const [state, dispatch] = useReducer(DataStoreReducer, initialStoreValue);

  useEffect(() => {
    if (!enableIssueTypeDetection) return;
    const projectId = state.userStoryState.currentProject?.id;
    if (!projectId) return;

    let isMounted = true;

    (async () => {
      const availability = await IssueTypeDetector.detectIssueTypes(projectId);
      if (!isMounted) return;
      dispatch({
        type: AppStoreActionTypes.SetIssueTypeAvailability,
        payload: availability
      });
    })();

    return () => {
      isMounted = false;
    };
  }, [dispatch, enableIssueTypeDetection, state.userStoryState.currentProject?.id]);

  return (
    <DataStoreContext.Provider value={{ state, dispatch }}>{children}</DataStoreContext.Provider>
  );
};

export const useDataStore = () => useContext(DataStoreContext);
